// gg-variant-tabs — turns the PDP variant picker into a tab UI (Größe / Farbe …)
// matching the Beschreibung/Anwendung tab strip: a horizontal label row with a
// thin baseline divider, one panel visible at a time.
//
// Unlike gg-info-tabs, this NEVER moves the live option fieldsets: they must stay
// inside <variant-picker>'s <form> so the native morph (which rebuilds that
// subtree on every selection and drives price/URL) keeps working. We only build
// a tab strip and toggle each panel's [hidden] in place.
//
// Panels, in document order:
//   1. an optional static Größe block (.gg-vt__static, single-size products)
//   2. each .gg-vs__option fieldset inside the picker form
// Tab label = data-gg-vt-label (static) or the option fieldset's legend (live).
class GgVariantTabs extends HTMLElement {
  connectedCallback() {
    this.#build();
  }

  #build() {
    const panels = this.#collectPanels();
    if (panels.length === 0) return;

    const strip = document.createElement('div');
    strip.className = 'gg-vt__strip';
    strip.setAttribute('role', 'tablist');

    this._panels = panels;
    panels.forEach((panel, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gg-vt__tab';
      btn.textContent = panel.label;
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      btn.addEventListener('click', () => this.#select(i));
      panel._btn = btn;
      strip.appendChild(btn);

      panel.el.classList.add('gg-vt__panel');
      panel.el.setAttribute('role', 'tabpanel');
      panel.el.hidden = i !== 0;

      // The selected-value indicator ("Farbe: …") lives outside the picker, so it
      // isn't a panel — pair it with its option and toggle it in lockstep, else it
      // leaks the colour name onto the Größe tab (and brings a second divider).
      if (panel.indicator) panel.indicator.hidden = i !== 0;
    });

    // Strip goes at the very top; panels stay exactly where they are (the static
    // block as our child, the option fieldsets inside the picker form).
    this.prepend(strip);
    this.classList.add('gg-vt--ready');

    this._active = 0;

    // The native <variant-picker> morphs its option subtree on every selection,
    // which rebuilds the .gg-vs__option nodes and wipes the [hidden] we set. The
    // morph mutates INTO the same elements, so our references survive — re-apply
    // the active state after each update so the inactive option can't reappear.
    // Defer to the next frame so we run AFTER the morph settles (same race the
    // color-sort in gg-variant-selector guards against).
    this.addEventListener('variant:update', () => {
      requestAnimationFrame(() => this.#apply());
    });
  }

  #collectPanels() {
    const panels = [];

    // 1. Static single-size block (optional), a direct child of this element.
    const staticEl = this.querySelector(':scope > .gg-vt__static');
    if (staticEl) {
      panels.push({ el: staticEl, label: staticEl.dataset.ggVtLabel || 'Größe' });
    }

    // 2. Live option fieldsets inside the picker — toggled in place. The Nth
    // indicator (rendered in option order, outside the picker) pairs with the Nth
    // option fieldset, so we can show each option's selected value only on its tab.
    const indicators = [...this.querySelectorAll('.gg-vs__indicator')];
    this.querySelectorAll('.gg-vs__option').forEach((opt, optIndex) => {
      panels.push({ el: opt, label: this.#labelFor(opt), indicator: indicators[optIndex] || null });
    });

    return panels;
  }

  #labelFor(optionEl) {
    const legend = optionEl.querySelector('.gg-vs__fieldset legend');
    const raw = legend ? legend.textContent.trim() : '';
    const lower = raw.toLowerCase();
    if (lower === 'color' || lower === 'farbe') return 'Farbe';
    return raw || 'Variante';
  }

  #select(index) {
    this._active = index;
    this.#apply();
  }

  // Reflect this._active onto tabs + panels. Idempotent, so it's safe to call
  // after a morph to restore [hidden] the picker may have stripped.
  #apply() {
    this._panels.forEach((panel, i) => {
      const active = i === this._active;
      panel._btn.setAttribute('aria-selected', active ? 'true' : 'false');
      panel.el.hidden = !active;
      if (panel.indicator) panel.indicator.hidden = !active;
    });
  }
}

if (!customElements.get('gg-variant-tabs')) {
  customElements.define('gg-variant-tabs', GgVariantTabs);
}
